<template>
  <div class="admin-page admin-page-panel">
    <p>{{ readOnly ? copy.readOnly : copy.help }}</p>
    <div v-if="!readOnly" class="admin-filter-bar">
      <a-select v-model="eventId" show-search :filter-option="false" :loading="loading" :placeholder="copy.choose" style="width:320px;max-width:100%" @search="loadEvents">
        <a-select-option v-for="event in events" :key="event.id" :value="event.id">{{ event.title }}</a-select-option>
      </a-select>
      <a-button type="primary" :disabled="!eventId" @click="openEvent">{{ copy.open }}</a-button>
      <a-button :loading="loading" @click="loadEvents()">{{ copy.refresh }}</a-button>
    </div>
    <template v-else>
      <a-button :loading="loading" @click="loadResults()">{{ copy.refresh }}</a-button>
      <a-table :data-source="rows" :columns="columns" row-key="id" :loading="loading" :pagination="pagination" :scroll="{ x: 780 }" @change="changePage" />
    </template>
    <a-alert v-if="error" type="error" show-icon :message="error" />
  </div>
</template>

<script>
import { getDragonEventList } from '@/api'
import request from '@/utils/request'
import { resultsWorkspaceLocation } from '@/utils/admin-routing'

// Compatibility URL only. All mutations use the same version-aware workbench;
// keeping a second editor here previously bypassed its required request fields.
const messages = {
  'zh-Hans': { help: '成绩录入、导入、复核与更正在赛事工作台中统一处理。请选择赛事继续，原有成绩保持不变。', choose: '搜索并选择赛事', open: '进入赛程与成绩', refresh: '刷新', readOnly: '当前账号可在此查看已授权成绩，但没有赛事工作台访问权限。本入口暂不提供编辑，请联系管理员核对角色权限。', event: '赛事', category: '组别', race: '场次', team: '队伍', rank: '名次', time: '成绩', state: '发布状态', published: '已发布', draft: '未发布' },
  'zh-Hant': { help: '成績錄入、匯入、覆核與更正在賽事工作台中統一處理。請選擇賽事繼續，原有成績保持不變。', choose: '搜尋並選擇賽事', open: '進入賽程與成績', refresh: '重新整理', readOnly: '目前帳號可在此查看已授權成績，但沒有賽事工作台存取權限。本入口暫不提供編輯，請聯絡管理員核對角色權限。', event: '賽事', category: '組別', race: '場次', team: '隊伍', rank: '名次', time: '成績', state: '發布狀態', published: '已發布', draft: '未發布' },
  en: { help: 'Enter, import, review and correct results in the event workspace. Choose an event to continue. Existing results are unchanged.', choose: 'Search and select an event', open: 'Open schedule & results', refresh: 'Refresh', readOnly: 'Results permitted for this account remain available here. Event workspace access is not granted. Editing is unavailable in this compatibility view; ask an administrator to review role permissions.', event: 'Event', category: 'Category', race: 'Race', team: 'Team', rank: 'Place', time: 'Time', state: 'Publication', published: 'Published', draft: 'Unpublished' }
}

export default {
  data: () => ({ eventId: undefined, events: [], loading: false, error: '', serial: 0, readOnly: false, rows: [], resultQuery: null, pagination: { current: 1, pageSize: 10, total: 0 } }),
  computed: {
    copy() { return messages[this.$adminLocale] || messages['zh-Hans'] },
    columns() { return [
      { title: this.copy.event, dataIndex: 'eventTitle' }, { title: this.copy.category, dataIndex: 'categoryName' },
      { title: this.copy.race, dataIndex: 'roundName', customRender: (value, row) => row.raceNo ? `Race ${row.raceNo}` : value || '—' },
      { title: this.copy.team, dataIndex: 'teamName' }, { title: this.copy.rank, dataIndex: 'rankNo', customRender: value => value > 0 ? value : '—' },
      { title: this.copy.time, dataIndex: 'resultTime', customRender: value => value || '—' },
      { title: this.copy.state, dataIndex: 'published', customRender: value => value === 1 ? this.copy.published : this.copy.draft }
    ] }
  },
  created() { this.openInitial() },
  beforeDestroy() { this.serial++ },
  watch: { '$route.query'(next, previous) { if (JSON.stringify(next) !== JSON.stringify(previous)) this.openInitial() } },
  methods: {
    denied(error) { return Number(error.businessCode) === 403 || (error.response && error.response.status === 403) },
    async openInitial() {
      this.readOnly = false; this.rows = []; this.resultQuery = null; this.pagination.current = 1
      const target = resultsWorkspaceLocation(this.$route.query)
      if (!target) return this.loadEvents()
      return this.openTarget(target)
    },
    async openTarget(target) {
      const serial = ++this.serial
      this.loading = true; this.error = ''
      try {
        // A result-only legacy role may read results without event:list. Verify
        // real workspace access before redirecting; never infer or grant it.
        await request({ url: '/dragonboat/event/detail', params: { id: Number(target.query.eventId) }, silentError: true })
        if (serial === this.serial) await this.$router.replace(target)
      } catch (error) {
        if (serial !== this.serial) return
        if (this.denied(error)) { this.readOnly = true; await this.loadResults({ ...target.query, published: this.$route.query.published }) }
        else this.error = error.message
      } finally { if (serial === this.serial) this.loading = false }
    },
    async loadEvents(title = '') {
      const serial = ++this.serial
      this.loading = true
      this.error = ''
      try {
        const response = await getDragonEventList({ page: 1, pageSize: 200, title })
        if (serial === this.serial) this.events = response.data.list || []
      } catch (error) {
        if (serial !== this.serial) return
        if (this.denied(error)) { this.readOnly = true; await this.loadResults() }
        else this.error = error.message
      }
      finally { if (serial === this.serial) this.loading = false }
    },
    openEvent() {
      const target = resultsWorkspaceLocation({ eventId: this.eventId })
      if (target) this.openTarget(target)
    },
    async loadResults(query = this.resultQuery || this.$route.query) {
      const serial = ++this.serial
      const target = resultsWorkspaceLocation(query)
      this.resultQuery = target ? target.query : {}
      if (['0', '1'].includes(String(query.published))) this.resultQuery.published = Number(query.published)
      this.loading = true; this.error = ''; this.rows = []
      try {
        const params = { page: this.pagination.current, pageSize: this.pagination.pageSize }
        for (const key of ['eventId', 'categoryId', 'roundId']) if (this.resultQuery[key]) params[key] = Number(this.resultQuery[key])
        if (this.resultQuery.published != null) params.published = this.resultQuery.published
        const response = await request({ url: '/dragonboat/result/list', params, silentError: true })
        if (serial === this.serial) { this.rows = response.data.list || []; this.pagination.total = response.data.total || 0 }
      } catch (error) { if (serial === this.serial) this.error = error.message }
      finally { if (serial === this.serial) this.loading = false }
    },
    changePage(pagination) {
      this.pagination.current = pagination.current
      this.pagination.pageSize = pagination.pageSize
      this.loadResults()
    }
  }
}
</script>
